/* import __COLOCATED_TEMPLATE__ from './brand-signup-company-match.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action, getProperties } from '@ember/object';
import { task } from 'ember-concurrency';
import { number, object, string } from 'yup';
import { validateSchema } from 'sh-www/utils/validators';
import { DEPARTMENTS } from 'sh-www/models/campaign';

const CLASS_NAME = 'GLM-brand-signup-company-match';

const SORTED_DEPARTMENTS = DEPARTMENTS.sort((a, b) => {
  if (a.key === b.key) {
    return 0;
  } else if (a.key === 'Other') {
    return 1;
  } else if (b.key === 'Other') {
    return -1;
  } else {
    return a.key.localeCompare(b.key);
  }
});

const SCHEMA = object({
  company: object().required('Please select or add a new company'),

  new_company_name: string().when(['company'], {
    is: (company) => company?.value == null,
    then: (schema) =>
      schema
        .required('Please enter a company name')
        .min(2, 'Please enter a company name'),
  }),

  department: number()
    .required('Please select a department')
    .oneOf(SORTED_DEPARTMENTS.map(({ value }) => value)),

  job_title: string()
    .required('Please enter a job title')
    .min(2, 'Please enter a job title'),
});

class BrandSignupCompanyMatch extends Component {
  @service('error-tracker') errorTracker;
  @service session;

  @tracked company = this.storedCompany;
  @tracked new_company_name = this.args.storedCompanyJoinRequest?.companyName ?? '';
  @tracked typed_department = null;
  @tracked selected_department = this.storedDepartment;
  @tracked job_title = this.args.storedCompanyJoinRequest?.jobTitle ?? '';
  @tracked errors = null;

  get className() {
    return CLASS_NAME;
  }

  get storedCompany() {
    const { storedCompanyJoinRequest } = this.args;

    if (storedCompanyJoinRequest) {
      const companyId = storedCompanyJoinRequest.companyId ?? null;
      return this.companies.find(company => company.value == companyId);
    }

    return null;
  }

  get storedDepartment() {
    const { storedCompanyJoinRequest } = this.args;

    if (storedCompanyJoinRequest?.department) {
      return SORTED_DEPARTMENTS.find(item => item.value == storedCompanyJoinRequest.department);
    }

    return null;
  }

  get submitDisabled() {
    return Boolean(
      this.hasErrors ||
      this.company == null ||
      (this.addNewCompany && this.new_company_name.length < 2) ||
      this.selected_department == null ||
      this.job_title.length < 2 ||
      this.storeOrUpdateCompanyJoinRequestTask.isRunning
    );
  }

  get hasErrors() {
    if (this.errors == null) {
      return false;
    }

    return Object.values(this.errors).some(value => value != undefined);
  }

  get companies() {
    return [...this.args.companyMatches, { key: '+ Add new company', value: null }];
  }

  get company_name() {
    return this.company?.key ?? '';
  }

  get addNewCompany() {
    return this.company?.value == null;
  }

  get filtered_departments() {
    const value = (this.typed_department ?? '').toLocaleLowerCase();

    return value
      ? SORTED_DEPARTMENTS.filter(({ key }) => key.toLocaleLowerCase().includes(value))
      : SORTED_DEPARTMENTS;
  }

  get department_name() {
    return this.typed_department ?? this.selected_department?.key ?? '';
  }

  get department() {
    return this.selected_department?.value;
  }

  // Error is not from input validation
  get genericError() {
    if (this.storeOrUpdateCompanyJoinRequestTask.last?.error && !this.hasErrors) {
      return 'There was a problem submitting your request. Please refresh the page and try again or contact support for help.';
    }

    return null;
  }

  @action
  setFormField(field, value) {
    this[field] = value;

    if (field === 'company') {
      this.new_company_name = '';
    }

    if (this.errors?.[field]) {
      this.errors = { ...this.errors, [field]: undefined };
    }
  }

  @action
  setDepartmentField(prop, value) {
    this[prop] = value;

    if (prop === 'typed_department' && value === '') {
      this.selected_department = null;
    } else if (prop === 'selected_department') {
      this.typed_department = value.key;
    }

    if (this.errors?.department) {
      this.errors = { ...this.errors, department: undefined };
    }
  }

  @task
  *storeOrUpdateCompanyJoinRequestTask(e) {
    e.preventDefault();

    const [_, validationErrors = null] = validateSchema(
      getProperties(this, Object.keys(SCHEMA.fields)),
      SCHEMA,
    );

    this.errors = validationErrors;

    if (this.errors) {
      return;
    }

    try {
      yield this.args.storeOrUpdateCompanyJoinRequest(
        this.company?.value ?? null,
        this.new_company_name,
        this.department,
        this.job_title,
      );
    } catch (error) {
      this.errorTracker.error(
        error,
        this,
        `storeOrUpdateCompanyJoinRequestTask failed for user id ${this.session.currentUser.id}}.`,
      );

      this.errors = {
        company: error.errors?.['company']?.[0],
        company_name: error.errors?.['company_name']?.[0],
        department: error.errors?.['department']?.[0],
        job_title: error.errors?.['job_title']?.[0],
      };

      throw error;
    }
  }
}

export default BrandSignupCompanyMatch;
